import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import AssignAgentsModal from 'components/common/group/content/AssignAgentsModal';
import { GroupSortBy, SortOrder } from 'types/groupService';
import { useBiDashboardPageStore } from 'store/pages/bi-groups-dashboard/pageStore';
import { SearchActionBar, TableBody, TableHeader } from '@insurancemenu/imenu-components-lib';
import { ContentWrapperStyled, HeadingsRowStyled, TableHeadingStyled, TableStyled, TableWrapperStyled } from './styles';
import BiTableRow from './BiTableRow';

const BiDashboardTable: React.FC = observer(() => {
  const {
    isLoading,
    groups,
    getInitialData,
    getAllAgencies,
    paginationDelegate: {
      page,
      sortBy,
      sortOrder,
      filters,
      totalPages,
      totalElements,
      setPage,
      setTotalPages,
      setFilterValue,
      setSortBy,
      setSortOrder,
    },
  } = useBiDashboardPageStore();

  // TO_DO: Refactor after group profile overhaul
  const {
    groupsDashboardController: { showAssignAgentsModal, closeAssignAgentsModal },
  } = useRootStore();

  useEffect(() => {
    void getAllAgencies();
  }, []);

  useEffect(() => {
    void getInitialData();
  }, [page]);

  useEffect(() => {
    setPage(1);
    setTotalPages(1);
    void getInitialData();
  }, [
    filters.filter,
    filters.agencyId,
    sortOrder,
    sortBy,
    filters.effectiveDate,
    filters.assignedGroups,
    filters.createdByMe,
    filters.name,
    filters.state,
    filters.carrierGroupNumber,
  ]);

  const onChangePageNumber = (newNumber: number): void => {
    setPage(newNumber);
  };

  const closeModal = (): void => {
    closeAssignAgentsModal();
    void getInitialData();
  };

  const onSearch = (name: string): void => {
    setPage(1);
    setFilterValue('name', name);
  };

  const onReset = (): void => {
    setPage(1);
    setFilterValue('name', '');
  };

  const onTableSort = (sortKey: string, sortingOrder: 'ASC' | 'DESC'): void => {
    setSortBy(sortKey as GroupSortBy);
    setSortOrder(sortingOrder as SortOrder);
  };

  const HEADINGS_MAP = [
    { title: 'Group Name', sorting: !!filters.agencyId, sortingKey: GroupSortBy.NAME },
    { title: 'Effective Dates', sorting: !!filters.agencyId, sortingKey: GroupSortBy.EFFECTIVE_DATE },
    { title: 'Product Lines' },
    { title: 'Stage', width: '6rem' },
    { title: 'Assigned', width: '8rem' },
    { title: 'Modified', sorting: !!filters.agencyId, sortingKey: GroupSortBy.UPDATE_TIME },
    { title: '', width: '5rem' },
  ];

  return (
    <ContentWrapperStyled>
      <TableWrapperStyled>
        <SearchActionBar
          totalElements={totalElements}
          totalTitle="Total Groups Count"
          searchPlaceholder="Group Name"
          isSearchButtonDisabled={isLoading}
          onSearch={onSearch}
          onReset={onReset}
        />
        <TableStyled
          loading={isLoading}
          maxHeight="55rem"
          totalElements={totalElements}
          showScrollShadows
          pagination={{
            onPageChange: onChangePageNumber,
            maxPage: totalPages || 1,
            tablePage: page,
            showPageInput: true,
          }}
        >
          <TableHeader>
            <HeadingsRowStyled>
              <TableHeadingStyled
                sorting={!!filters.agencyId}
                sortingKey={GroupSortBy.STATE}
                onSortCallback={filters.agencyId ? onTableSort : undefined}
                key="State"
                width="4rem"
              >
                State
              </TableHeadingStyled>
              <TableHeadingStyled>Agency Name</TableHeadingStyled>
              {HEADINGS_MAP.map((headingObj) => (
                <TableHeadingStyled
                  {...headingObj}
                  onSortCallback={headingObj.sorting && filters.agencyId ? onTableSort : undefined}
                  key={headingObj.title}
                >
                  {headingObj.title}
                </TableHeadingStyled>
              ))}
            </HeadingsRowStyled>
          </TableHeader>
          <TableBody>
            {groups.map((group) => {
              return <BiTableRow groupDelegate={group} key={group.data.id} />;
            })}
          </TableBody>
        </TableStyled>
      </TableWrapperStyled>
      <AssignAgentsModal show={showAssignAgentsModal} onClose={closeModal} isClosable />
    </ContentWrapperStyled>
  );
});

export default BiDashboardTable;
